import {
  ANDROID_PACKAGE,
  APP_NAME,
  FB_APP_ID,
  FB_PAGES,
  IOS_APP_STORE_ID,
  ITUNES_APP,
  OG_IMAGE,
  OG_SITE_NAME,
  ROBOTS,
  TWITTER_CARD,
} from "@elements/MetaTags/MetaTagsConstants";
import { BH_BASE_URL } from "@helper/getEnvVariables";
import type { GADataProps } from "@typings/GA";
import { MetaTagsProps } from "@typings/MetaTags";

import { getCookieByName } from "./utils";

export function getMetaData(metaTags: MetaTagsProps) {
  const origin = BH_BASE_URL;
  const canonicalUrl: string =
    origin + (metaTags.slug === "home" ? "" : metaTags.slug);
  const shortLinkUrl: string = metaTags.shortLink
    ? metaTags.shortLink
    : canonicalUrl;
  const image: string = metaTags.image?.url
    ? typeof metaTags.image.url === "string"
      ? metaTags.image.url
      : String(metaTags.image.url)
    : origin + OG_IMAGE;
  const ogImage: string = metaTags.ogImage?.url
    ? typeof metaTags.ogImage.url === "string"
      ? metaTags.ogImage.url
      : String(metaTags.ogImage.url)
    : origin + OG_IMAGE;

  const metaAttributes: Array<Record<string, string>> = [
    { name: "robots", content: metaTags.robots || ROBOTS },
    { property: "og:site_name", content: OG_SITE_NAME },
    { property: "og:url", content: canonicalUrl },
    { property: "og:title", content: metaTags.title },
    { property: "og:image", content: ogImage },
    { property: "og:image:url", content: String(ogImage) },
    { property: "og:image:secure_url", content: String(ogImage) },
    {
      property: "twitter:card",
      content: metaTags.twitterCardContent || TWITTER_CARD,
    },
    { property: "twitter:title", content: metaTags.title },
    { property: "twitter:url", content: canonicalUrl },
    {
      property: "og:image:width",
      content: metaTags.ogImage?.width ? String(metaTags.ogImage.width) : "",
    },
    {
      property: "og:image:height",
      content: metaTags.ogImage?.height ? String(metaTags.ogImage.height) : "",
    },

    // Facebook / app links
    { property: "fb:pages", content: FB_PAGES },
    { property: "fb:app_id", content: FB_APP_ID },

    // App links & store ids
    { property: "al:ios:url", content: canonicalUrl, "data-rh": "true" },
    {
      property: "al:ios:app_store_id",
      content: IOS_APP_STORE_ID,
      "data-rh": "true",
    },
    { name: "apple-itunes-app", content: ITUNES_APP },
    { property: "al:ios:app_name", content: APP_NAME, "data-rh": "true" },
    { property: "al:android:url", content: canonicalUrl },
    { property: "al:android:package", content: ANDROID_PACKAGE },
    { name: "google-play-app", content: ANDROID_PACKAGE },
    { property: "al:android:app_name", content: APP_NAME },
    { property: "al:web:url", content: canonicalUrl },
  ];

  if (metaTags.isArticlePage) {
    metaAttributes.push(
      { name: "dcterms.title", content: metaTags.title },
      { name: "dcterms.creator", content: "cue" },
      { name: "dcterms.description", content: metaTags.description || "" },
      { name: "dcterms.date", content: metaTags.publishedTime || "" },
      { name: "dcterms.type", content: "text" },
      { name: "dcterms.format", content: "text/html" },
      { name: "dcterms.identifier", content: canonicalUrl },
      { name: "article:published_time", content: metaTags.publishedTime || "" },
      { name: "article:modified_time", content: metaTags.modifiedTime || "" },
    );
  }
  if (metaTags.description) {
    metaAttributes.push(
      { name: "description", content: metaTags.description },
      { name: "twitter:description", content: metaTags.description },
    );
  }
  (metaTags.ogDescription || metaTags.description) &&
    metaAttributes.push({
      name: "og:description",
      content: metaTags.ogDescription || metaTags.description || "",
    });
  if (metaTags.keywords != undefined && metaTags.keywords.length > 0) {
    metaAttributes.push({
      name: "keywords",
      content: metaTags.keywords.join(", "),
    });
    metaAttributes.push({
      name: "news_keywords",
      content: metaTags.keywords.join(", "),
    });
  }
  metaTags.ogType &&
    metaAttributes.push({ property: "og:type", content: metaTags.ogType });
  metaTags.modifiedTime &&
    metaAttributes.push({
      property: "og:updated_time",
      content: metaTags.modifiedTime,
    });
  metaTags.addAppleMobileTags &&
    metaAttributes.push(
      { name: "apple-mobile-web-app-capable", content: "yes" },
      {
        name: "apple-mobile-web-app-titlee",
        content: "Berita Harian",
      },
    );

  // TODO: unable to add the id and data-testid attributes to this meta tag
  metaTags.gsChannels &&
    metaAttributes.push({
      name: "grapeshot_tag",
      content: metaTags.gsChannels.join(","),
    });

  const linkAttributes: Array<Record<string, string>> = [
    { tagName: "link", rel: "manifest", href: "/manifest.json" },
    { tagName: "link", rel: "canonical", href: canonicalUrl },
  ];

  if (image) {
    linkAttributes.push({
      tagName: "link",
      rel: "image_src",
      href: image,
      "data-rh": "true",
    });
  }
  metaTags.shortLink &&
    linkAttributes.push({
      tagName: "link",
      rel: "shortlink",
      href: shortLinkUrl,
    });

  return [{ title: metaTags.title }, ...metaAttributes, ...linkAttributes];
}

export function getGTMData(props: GADataProps): GADataProps {
  const {
    section = "",
    level2 = "",
    chapter1 = "", // Section child category name.
    chapter2 = "",
    chapter3 = "",
    at = getCookieByName("mysphw") ?? "", // mysphw cookie
    visitorcat = Number(getCookieByName("visitorcat")),
    svc = getCookieByName("svc") ?? "",
    title = "", // can contain spaces
    articleid = "",
    author = "", // Comma separated author name
    contentcat = "1", // Paid (2) or free (1)
    contenttype = "listing", // index/listing, article
    pubdate = "", // Post date of article otherwise ""
    keyword = "", // Comma separated keyword names
    pagination = 1,
    abVariant = "BH-DEFAULT",
    cue_articleid = "",
    cd_version = "bh2",
    cd_language = "ms",
    content_publication_date_utc = "",
    content_update_date_utc = "",
  } = props;

  const gaData: GADataProps = {
    section,
    chapter1,
    chapter2,
    chapter3,
    at,
    visitorcat,
    svc,
    abVariant,
    contenttype,
    title,
    contentcat,
    level2,
    keyword,
    author,
    pubdate,
    articleid,
    cue_articleid,
    cd_version,
    cd_language,
    pagination,
    content_publication_date_utc,
    content_update_date_utc,
  };

  return gaData;
}
